/* BD Fitment core — shared by the Wheel Finder page and the PDP buy box.
   Owns: wheel-size API access, vehicle profile building (per-axle envelopes),
   part grading (drilled exact / blank drillable ranges), and the persisted
   vehicle selection (localStorage) that lets the PDP inherit the finder's car. */
window.BDFitCore = (() => {
  const API = "https://api.wheel-size.com/v2";
  const STORE_KEY = "bdf_vehicle_v4"; // v4: trim-aware, USDM only
  const REGION = "usdm"; // single-market store: always US-spec fitment data

  // key precedence: ?ws_key= (testing) > localStorage bdf_ws_key (local dev,
  // set once in DevTools) > theme setting injected by Liquid
  const resolveKey = (injected) => {
    try {
      return new URLSearchParams(location.search).get("ws_key")
        || localStorage.getItem("bdf_ws_key") || injected || "";
    } catch (_) { return injected || ""; }
  };

  // makes BD never sells to — hidden from every picker (slugs per wheel-size API)
  const EXCLUDED_MAKES = ["abarth", "byd", "panoz", "morgan"];

  const makeApi = (key) => async (path, params) => {
    const q = new URLSearchParams({ ...params, user_key: key });
    const r = await fetch(`${API}${path}?${q}`);
    if (!r.ok) throw new Error(`API ${r.status}`);
    const data = (await r.json()).data;
    if (path === "/makes/" && Array.isArray(data)) {
      return data.filter((m) => !EXCLUDED_MAKES.includes(m.slug));
    }
    return data;
  };

  const normPat = (p) => String(p).toLowerCase().replace(/\s/g, "").replace(/x0+(\d)/, "x$1");
  const parsePat = (p) => { const m = /^(\d+)x([\d.]+)$/.exec(normPat(p)); return m ? [+m[1], +m[2]] : null; };
  const parseSize = (s) => { const m = /^([\d.]+)[×x]([\d.]+)$/.exec(String(s)); return m ? { d: +m[1], w: +m[2] } : null; };

  const envelope = (sizes) => {
    if (!sizes.length) return null;
    const dm = sizes.map((s) => s.d), wm = sizes.map((s) => s.w).filter((x) => x != null),
      om = sizes.map((s) => s.o).filter((x) => x != null);
    return { dMin: Math.min(...dm), dMax: Math.max(...dm),
      wMin: wm.length ? Math.min(...wm) : null, wMax: wm.length ? Math.max(...wm) : null,
      oMin: om.length ? Math.min(...om) : Infinity, oMax: om.length ? Math.max(...om) : -Infinity };
  };

  const trimsOf = (mods) => [...new Set(mods.map((m) => m.trim || m.name))];
  const trimMods = (mods, trim) => {
    if (!trim) return mods;
    const sel = mods.filter((m) => (m.trim || m.name) === trim);
    return sel.length ? sel : mods;
  };

  function buildVehicle(mods, yearLabel, trimLabel) {
    const patterns = new Set(); let bore = 0; const trims = [];
    // per axle: `oe` = EVERY row wheel-size lists for the trim (factory, optional
    // OEM packages, documented aftermarket) — all feed the envelope (Cody/Eric
    // 2026-10-15: use the most liberal values wheel-size returns; BD would rather
    // say "fits" than be conservative — optional packages were coming back
    // is_stock=false and shrinking the window). `alt` still holds the non-stock
    // rows as exact points for the size rescue (now redundant, kept harmless).
    const ax = { front: { oe: [], alt: [] }, rear: { oe: [], alt: [] } };
    mods.forEach((m) => {
      const t = m.technical || {};
      if (t.bolt_pattern && t.bolt_pattern !== "N/A") patterns.add(normPat(t.bolt_pattern));
      const cb = parseFloat(t.centre_bore); if (!isNaN(cb)) bore = Math.max(bore, cb);
      trims.push(m.trim || m.name);
      (m.wheels || []).forEach((wh) => {
        const buckets = wh.is_stock === false ? ["oe", "alt"] : ["oe"];
        const r = wh.rear && wh.rear.rim_diameter ? wh.rear : wh.front;
        for (const bucket of buckets) {
          if (wh.front && wh.front.rim_diameter) ax.front[bucket].push({ d: wh.front.rim_diameter, w: wh.front.rim_width, o: wh.front.rim_offset });
          if (r && r.rim_diameter) ax.rear[bucket].push({ d: r.rim_diameter, w: r.rim_width, o: r.rim_offset });
        }
      });
    });
    // envelope (sizes AND offsets) from every row for the trim; `alt` = the
    // non-stock subset, kept as exact documented size points for the rescue
    const mk = (a) => {
      const oe = a.oe;
      const e = envelope(oe);
      return e && { ...e, alt: a.alt.filter((p, i, arr) =>
        arr.findIndex((q) => q.d === p.d && q.w === p.w) === i) };
    };
    const tag = (e, axle) => e && { ...e, axle };
    const front = mk(ax.front), rear = mk(ax.rear);
    const all = tag(mk({ oe: [...ax.front.oe, ...ax.rear.oe], alt: [...ax.front.alt, ...ax.rear.alt] }), "all");
    return { label: `${yearLabel} ${mods[0].make.name} ${mods[0].model.name}${trimLabel ? ` ${trimLabel}` : ""}`,
      gen: mods[0].generation && mods[0].generation.name,
      patterns: [...patterns], bore, trims: [...new Set(trims)],
      axles: { front: tag(front, "front") || all, rear: tag(rear, "rear") || all, all },
      ...all }; // top-level = combined envelope, keeps older callers working
  }

  // ---- graders (envelope `v` needs patterns/bore + dMin..oMax) ----
  // widths widen per axle: fronts get +1.5" over OEM max, rears +2".
  // The combined "all" envelope (finder cards, no axle context) uses the rear
  // allowance — it's a union view; the PDP re-grades per axle strictly.
  // Offsets: −25 mm below factory / −12 mm below a documented aftermarket
  // offset (whichever reaches lower), +8 mm above the highest of either.
  const K = { plus: 2, wlo: 0.5, whiFront: 1.5, whiRear: 2, olo: 25, oloAlt: 12, ohi: 8 };
  const whiFor = (v) => (v.axle === "front" ? K.whiFront : K.whiRear);
  const offsetWindow = (v) => {
    const aos = (v.alt || []).map((p) => p.o).filter((x) => x != null);
    let lo = isFinite(v.oMin) ? v.oMin - K.olo : Infinity;
    let hi = isFinite(v.oMax) ? v.oMax + K.ohi : -Infinity;
    if (aos.length) {
      lo = Math.min(lo, Math.min(...aos) - K.oloAlt);
      hi = Math.max(hi, Math.max(...aos) + K.ohi);
    }
    return { lo, hi, known: isFinite(lo) && isFinite(hi) };
  };
  // A size the OEM tolerance box rejects still fits when wheel-size documents it
  // as a real-world aftermarket fitment for this axle (exact diameter, width no
  // wider than the documented point). Always a "warn" — it's beyond factory spec.
  const altFit = (size, v) => (v.alt || []).some((p) =>
    size.d === p.d && (size.w == null || (size.w <= p.w && size.w >= p.w - K.wlo)));
  function sizeChecks(size, v, reasons) {
    const rescue = () => {
      if (!altFit(size, v)) return false;
      reasons.length = 0; reasons.stretched = true;
      reasons.push(`Documented aftermarket fitment ${size.d}x${size.w}"`);
      return true;
    };
    const dOK = size.d >= v.dMin && size.d <= v.dMax, dTol = size.d > v.dMax && size.d <= v.dMax + K.plus;
    if (!dOK && !dTol) {
      if (rescue()) return true;
      reasons.push(`${size.d}" outside ${v.dMin}–${v.dMax}" (+${K.plus}")`); return false;
    }
    if (dTol) { reasons.stretched = true; reasons.push(`Plus-size ${size.d}" vs OEM max ${v.dMax}"`); }
    if (size.w != null && v.wMin != null) {
      const wLo = v.wMin - K.wlo, wHi = v.wMax + whiFor(v);
      if (size.w < wLo || size.w > wHi) {
        if (rescue()) return true;
        reasons.push(`Width ${size.w}" outside ${wLo}–${wHi}"`); return false;
      }
      if (size.w < v.wMin || size.w > v.wMax) { reasons.stretched = true; reasons.push(`Width ${size.w}" vs OEM ${v.wMin}–${v.wMax}"`); }
    }
    return true;
  }
  // opts.ignoreOffset — Off-Road series (Eric/Cody 2026-10-15): BD's off-road
  // parts are cut at ET+1 / ET−12 for trucks whose factory offsets sit at
  // ET34–55, so the offset window rejected every modern F-150/Silverado/Ram.
  // Decision: skip the offset check entirely for Off-Road parts (pattern, hub,
  // diameter and width still apply). Set from the product's "Off-Road Series"
  // tag / bd-o* handle by every consumer, or stamped on the part as part.offroad.
  const skipOffset = (part, opts) => !!((opts && opts.ignoreOffset) || (part && part.offroad));
  function gradeDrilled(part, size, v, opts) {
    const reasons = []; reasons.stretched = false;
    // Dual-pattern wheels must carry the vehicle's matching pattern into the
    // cart/order, rather than whichever pattern is listed first on the part.
    const pattern = part.patterns.map(normPat).find((p) => v.patterns.includes(p));
    if (!pattern)
      return { verdict: "no", reasons: [`Bolt pattern ${part.patterns.join("/")} ≠ ${v.patterns.join("/")}`] };
    let ring = false;
    if (part.hub != null && v.bore) {
      if (part.hub < v.bore - 0.5) return { verdict: "no", reasons: [`Hub bore ${part.hub} < vehicle ${v.bore}`] };
      if (part.hub > v.bore + 0.5) ring = true;
    }
    if (!sizeChecks(size, v, reasons)) return { verdict: "no", reasons: [reasons[reasons.length - 1]] };
    const ow = offsetWindow(v);
    if (part.offset != null && ow.known && !skipOffset(part, opts)) {
      if (part.offset < ow.lo || part.offset > ow.hi) return { verdict: "no", reasons: [`Offset ET${part.offset} outside ${ow.lo}–${ow.hi}`] };
      if (part.offset < v.oMin || part.offset > v.oMax) { reasons.stretched = true; reasons.push(`Offset ET${part.offset} vs OEM ET${v.oMin}–${v.oMax}`); }
    }
    if (ring) reasons.push(`Needs ${part.hub}→${v.bore} hub rings`);
    reasons.unshift(`${part.patterns.join("/")} · ET${part.offset}${part.qty > 0 ? " · in stock" : ""}`);
    return { verdict: reasons.stretched ? "warn" : "ok", reasons, pattern, offset: part.offset, hub: part.hub, ring };
  }
  function gradeBlank(part, size, v, opts) {
    const reasons = []; reasons.stretched = false;
    let recPat = null;
    for (const vp of v.patterns) {
      const pp = parsePat(vp); if (!pp) continue;
      for (const rg of part.patterns) if (pp[0] === rg.lugs && pp[1] >= rg.min - 0.5 && pp[1] <= rg.max + 0.5) { recPat = vp; break; }
      if (recPat) break;
    }
    if (!recPat) {
      const rs = part.patterns.map((r) => `${r.lugs}x${r.min}–${r.max}`).join(", ");
      return { verdict: "no", reasons: [`Not drillable to ${v.patterns.join("/")} (range ${rs})`] };
    }
    if (!sizeChecks(size, v, reasons)) return { verdict: "no", reasons: [reasons[reasons.length - 1]] };
    const ow = skipOffset(part, opts) ? { lo: -Infinity, hi: Infinity, known: false } : offsetWindow(v);
    const bLo = Math.max(part.offset_min, ow.lo), bHi = Math.min(part.offset_max, ow.hi);
    if (bLo > bHi) return { verdict: "no", reasons: [`Buildable ET${part.offset_min}–${part.offset_max} can't reach ET${ow.lo}–${ow.hi}`] };
    let recOff = Math.round(Math.min(Math.max((v.oMin + v.oMax) / 2, bLo), bHi));
    if (recOff < v.oMin || recOff > v.oMax) { reasons.stretched = true; reasons.push(`Built ET${recOff} (OEM ET${v.oMin}–${v.oMax})`); }
    let ring = false;
    if (part.hub_min != null) {
      if (v.bore > part.hub_max + 0.5) return { verdict: "no", reasons: [`Hub ${v.bore} exceeds max bore ${part.hub_max}`] };
      if (v.bore < part.hub_min - 0.5) ring = true;
    }
    reasons.unshift(`Drill to ${recPat} · ET${recOff}${ring ? ` · ${part.hub_min}mm bore + rings` : ` · bore ${v.bore}`}`);
    return { verdict: "built", recPat, recOff, reasons, pattern: recPat, offset: recOff, hub: v.bore, ring };
  }

  const RANK = { ok: 0, warn: 1, built: 2, no: 3 };
  // best verdict across a variant's parts against one envelope (axle or combined)
  function gradeParts(stock, size, env, patterns, bore, opts) {
    const v = { ...env, patterns, bore };
    let best = null, anyStock = false;
    for (const part of stock || []) {
      const g = part.kind === "blank" ? gradeBlank(part, size, v, opts) : gradeDrilled(part, size, v, opts);
      g.part = part.part;
      // BD labeling rule (client, 2026-08-24): a fitting part with inventory is
      // "in stock" whether it's drilled OR an undrilled blank — drilling a
      // blank on the shelf takes no meaningful time. anyStock carries that.
      if (g.verdict !== "no" && (part.qty ?? 0) > 0) anyStock = true;
      if (!best || RANK[g.verdict] < RANK[best.verdict]) best = g;
      if (best.verdict === "ok" && anyStock) break;
    }
    const out = best || { verdict: "no", reasons: ["No part data"] };
    out.anyStock = anyStock;
    return out;
  }

  // A design must offer a front AND rear option in the same finish family.
  // Callers can pass expanded stock objects or dictionary-indexed CDN data.
  const finishFamily = (finish) => String(finish || "").replace(/ (SDP|DP|FP)(?= |$)/g, "").replace(/ (LEFT|RIGHT)$/, "").trim();
  function gradeDesign(product, vehicle, parts) {
    if (!product || !vehicle) return { verdict: "unknown", configurations: [] };
    const groups = new Map(); let graded = false;
    const opts = { ignoreOffset: isOffroad(product) };
    for (const variant of product.variants || []) {
      const stock = (variant.stock || []).map(p => typeof p === "number" ? parts?.[p] : p).filter(Boolean);
      if (stock.length) graded = true;
      const size = parseSize(variant.size); if (!size || !stock.length) continue;
      const family = finishFamily(variant.finish);
      if (!groups.has(family)) groups.set(family, { finish: family, front: [], rear: [] });
      const group = groups.get(family);
      for (const axle of ["front", "rear"]) {
        const grade = gradeParts(stock, size, vehicle.axles?.[axle] || vehicle, vehicle.patterns, vehicle.bore, opts);
        if (grade.verdict !== "no") group[axle].push({ variant, grade });
      }
    }
    const configurations = [...groups.values()].filter(g => g.front.length && g.rear.length);
    if (!configurations.length) return { verdict: graded ? "no" : "unknown", configurations };
    const rank = Math.min(...configurations.map(g => Math.max(
      Math.min(...g.front.map(v => RANK[v.grade.verdict])),
      Math.min(...g.rear.map(v => RANK[v.grade.verdict])))));
    return { verdict: Object.keys(RANK).find(k => RANK[k] === rank), configurations };
  }

  // Complete-set grading (clearance/overstock set listings): positions are
  // COMPLEMENTARY axle fitments, each with its own size and its own candidate
  // parts. Within a position: best-of alternatives (gradeParts, same as
  // everywhere else). Across positions: worst-of — a set fits only if every
  // position fits its axle envelope.
  // positions: [{ axle: "front"|"rear"|"all", size: "20x10", parts: [stock…] }]
  function gradeSetPositions(positions, vehicle, opts) {
    let worst = null;
    for (const pos of positions || []) {
      const size = parseSize(pos.size);
      if (!size) return { verdict: "no", reasons: [`Bad size data: ${pos.size}`] };
      const env = (pos.axle && vehicle.axles && vehicle.axles[pos.axle])
        || (vehicle.axles && vehicle.axles.all) || vehicle;
      const g = gradeParts(pos.parts, size, env, vehicle.patterns, vehicle.bore, opts);
      g.axle = pos.axle;
      if (!worst || RANK[g.verdict] > RANK[worst.verdict]) worst = g;
    }
    return worst || { verdict: "no", reasons: ["No set data"] };
  }

  // ---- persisted vehicle selection ----
  // bd:vehicle-changed fires on every save/clear so page-level listeners
  // (vehicle concierge, bars) can react without each surface wiring callbacks
  const announce = () => { try { window.dispatchEvent(new CustomEvent("bd:vehicle-changed")); } catch (_) {} };
  const saveVehicle = (data) => { try { localStorage.setItem(STORE_KEY, JSON.stringify({ savedAt: Date.now(), ...data })); } catch (_) {} announce(); };
  const loadVehicle = () => { try { return JSON.parse(localStorage.getItem(STORE_KEY)); } catch (_) { return null; } };
  const clearVehicle = () => { try { localStorage.removeItem(STORE_KEY); } catch (_) {} announce(); };

  // ---- vehicle <-> URL params (deep links for sharing and ads) ----
  const writeVehicleToUrl = (saved) => {
    const p = new URLSearchParams(location.search);
    ["make", "model", "year", "trim", "region"].forEach((k) => p.delete(k));
    if (saved && saved.slugs) {
      p.set("make", saved.slugs.make); p.set("model", saved.slugs.model); p.set("year", saved.slugs.year);
      if (saved.trim) p.set("trim", saved.trim);
    }
    history.replaceState(null, "", `${location.pathname}${p.toString() ? "?" + p : ""}`);
  };
  const vehicleLinkParams = () => {
    const s = loadVehicle();
    if (!s || !s.slugs) return "";
    const p = new URLSearchParams({ make: s.slugs.make, model: s.slugs.model, year: s.slugs.year });
    if (s.trim) p.set("trim", s.trim);
    return p.toString();
  };
  // ?make=&model=&year=[&trim=] on ANY page selects that vehicle (ad landings).
  // Uses the saved copy when it matches, else one API call. No trim = all trims.
  async function resolveVehicleFromUrl(api) {
    const q = new URLSearchParams(location.search);
    const mk = q.get("make"), md = q.get("model"), yr = q.get("year");
    if (!mk || !md || !yr) return null;
    const tr = q.get("trim") || null;
    const saved = loadVehicle();
    if (saved && saved.slugs && saved.slugs.make === mk && saved.slugs.model === md
        && saved.slugs.year === yr && (saved.trim || null) === tr) return saved;
    if (!api) return null;
    const mods = await api("/search/by_model/", { make: mk, model: md, year: yr, region: REGION });
    if (!mods || !mods.length) return null;
    const sel = trimMods(mods, tr); // falls back to all trims if tr doesn't match
    saveVehicle({ slugs: { make: mk, model: md, year: yr }, trim: tr,
      vehicle: buildVehicle(sel, yr, sel === mods ? null : tr) });
    return loadVehicle();
  }

  // ---- compact year/make/model/trim picker (USDM only) ----
  // onPartial (optional) fires when an upstream change invalidates the resolved
  // vehicle — hosts should drop vehicle-based filtering until onVehicle fires again.
  // onProvisional (optional) fires when the model resolves but a trim is still
  // pending: it carries the all-trims union vehicle so hosts can filter on what
  // is already certain (a wheel fitting NO trim of the model can hide now).
  function mountPicker(container, { api, onVehicle, onPartial, onProvisional, persist = true, initial = null }) {
    container.innerHTML = `
      <select data-r="year" disabled><option>Year</option></select>
      <select data-r="make" disabled><option value="">Make</option></select>
      <select data-r="model" disabled><option value="">Model</option></select>
      <select data-r="trim" disabled><option value="">Trim</option></select>`;
    const el = (r) => container.querySelector(`[data-r="${r}"]`);
    const opt = (v, t) => Object.assign(document.createElement("option"), { value: v, textContent: t });
    const fill = (sel, items, ph) => { sel.innerHTML = ""; sel.appendChild(opt("", ph)); items.forEach((i) => sel.appendChild(opt(i.slug, i.name))); sel.disabled = !items.length; };
    const LABEL = { make: "Make", model: "Model", year: "Year", trim: "Trim" };
    const reset = (...rs) => rs.forEach((r) => { el(r).innerHTML = `<option value="">${LABEL[r]}</option>`; el(r).disabled = true; });
    let mods = null; // modifications for the selected year, trim options come from here
    function resolve() {
      const tr = el("trim").value;
      if (!mods || !tr) return;
      const trim = tr === "*" ? null : tr;
      const vehicle = buildVehicle(trimMods(mods, trim), el("year").value, trim);
      const saved = { slugs: { make: el("make").value, model: el("model").value, year: el("year").value }, trim, vehicle };
      if (persist) saveVehicle(saved);
      onVehicle(vehicle, saved);
    }
    const invalidate = () => { if (onPartial) onPartial(); };
    el("year").onchange = async () => { mods = null; reset("make", "model", "trim"); invalidate(); if (el("year").value) fill(el("make"), await api("/makes/", { year: el("year").value, region: REGION }), "Make"); };
    el("make").onchange = async () => {
      mods = null; reset("model", "trim"); invalidate();
      if (!el("make").value) return;
      fill(el("model"), await api("/models/", { make: el("make").value, year: el("year").value, region: REGION }), "Model");
    };
    el("model").onchange = async () => {
      mods = null; reset("trim"); invalidate();
      if (!el("model").value) return;
      const got = await api("/search/by_model/", { make: el("make").value, model: el("model").value, year: el("year").value, region: REGION });
      if (!got || !got.length) { onVehicle(null, { empty: true }); return; }
      mods = got;
      const trims = trimsOf(mods);
      if (trims.length === 1) { // nothing to choose — resolve straight away
        fill(el("trim"), [{ slug: trims[0], name: trims[0] }], "Trim");
        el("trim").value = trims[0];
        resolve();
      } else {
        fill(el("trim"), trims.map((t) => ({ slug: t, name: t })).concat({ slug: "*", name: "Not sure — all trims" }), "Trim");
        if (onProvisional) onProvisional(buildVehicle(mods, el("year").value, null),
          { make: el("make").value, model: el("model").value, year: el("year").value });
      }
    };
    el("trim").onchange = () => { el("trim").value ? resolve() : invalidate(); };
    const ready = api("/years/", { region: REGION })
      .then(async (ys) => {
        fill(el("year"), ys.map((y) => ({ slug: String(y.slug), name: String(y.name) })), "Year");
        if (!initial?.slugs) return;
        for (const field of ["year", "make", "model"]) {
          const value = String(initial.slugs[field] || "");
          if (![...el(field).options].some(o => o.value === value)) return;
          el(field).value = value;
          await el(field).onchange();
        }
        const trim = initial.trim || "*";
        if ([...el("trim").options].some(o => o.value === trim)) { el("trim").value = trim; resolve(); }
      })
      .catch((e) => { el("year").innerHTML = `<option>Failed (${e.message})</option>`; });
    return { el, ready };
  }

  // Off-Road detection shared by consumers: product tag or handle
  const isOffroad = (p) => !!(p && ((p.tags || []).some((t) => /off-?road/i.test(t)) || /^bd-o\d/i.test(p.handle || "") || p.offroad === true));

  return { isOffroad, makeApi, resolveKey, normPat, parsePat, parseSize, buildVehicle, trimsOf, trimMods, REGION, gradeDrilled, gradeBlank, gradeParts, gradeSetPositions,
    RANK, finishFamily, gradeDesign, saveVehicle, loadVehicle, clearVehicle, mountPicker,
    writeVehicleToUrl, vehicleLinkParams, resolveVehicleFromUrl };
})();
